import React, { createContext, useContext, useEffect, useRef, useState } from 'react';
import { identifyUser, resetIdentity } from '../config/posthog';
import {
  signIn,
  signOut,
  confirmSignUp,
  resendSignUpCode,
  resetPassword,
  confirmResetPassword,
  fetchAuthSession,
  getCurrentUser,
} from 'aws-amplify/auth';
import { API_BASE_URL } from '../config/api';
import { VAULT_INTENT_KEY } from '../services/vaultService';
import { clearPendingOAuthRequest } from '../services/oauthService';
import { ACCOUNT_PENDING_DELETION_EVENT, SESSION_EXPIRED_EVENT, markSessionExpiredNotice, sessionEndReasonOf, SessionEndReason } from './sessionExpiry';
import type { PendingDeletion } from '../services/accountService';
import { IDLE_CHECK_INTERVAL_MS, isIdleExpired, recordActivity, trackActivity } from './idleTimeout';
import { signUpWithSignUpClient } from './cognitoSignUp';
import { TERMS_VERSION } from '../config/legal';
import type { UsageSnapshot } from '../utils/usage';

/**
 * "Log out" ends every session of the account, not just this browser
 * (pentest 2026-10-30 AUTH-6, AUTH-9): the API first refuses the account's
 * current tokens, then Cognito revokes every refresh token (GlobalSignOut).
 * The session is read first so an access token older than an hour is
 * refreshed: GlobalSignOut needs a live one. Amplify clears this browser
 * whatever Cognito answers.
 */
async function signOutEverywhere() {
  try {
    const token = (await fetchAuthSession()).tokens?.accessToken?.toString();
    if (token) {
      await fetch(`${API_BASE_URL}api/auth/logout`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
    }
  } catch (error) {
    console.error('Could not end the sessions on the server:', error);
  }
  await signOut({ global: true });
}

// Interface for user profile from database
interface UserProfile {
  id: string;
  email: string;
  fullName: string;
  // Public brand name (#395), set in Settings; absent until the owner saves one.
  displayName?: string;
  // The brand's logos (as URLs) and colours (#462); owners only, absent until saved.
  brand?: {
    logoSquareUrl?: string;
    logoWideUrl?: string;
    primaryColor?: string;
    accentColor?: string;
  };
  role: 'admin' | 'user';
  tokenBalance: number;
  walletAddress?: string;
  isSubUser?: boolean;
  /** 'admin' when the owner named this member an admin (#459). */
  teamRole?: 'admin';
  maxSubUsers?: number;
  qrStyle?: Record<string, any>;
  theme?: 'light' | 'dark';
  // Effective per-account feature state (issue #236); absent key = enabled
  features?: Record<string, boolean>;
  // Consumer Vault: absent on legacy rows ⇒ treated as 'client'.
  accountType?: 'client' | 'consumer';
  // Subscriber API-key entitlement (#227), admin-set. Already present in the
  // /users/me payload — this only types it so the Settings panel can read it.
  apiAccessEnabled?: boolean;
  subscriptionStatus?: 'active' | 'inactive' | 'past_due' | 'canceled' | 'expired';
  // Plan (#395): set on client accounts created since #395, absent on legacy
  // rows, team members and consumers. The backend adds the display name.
  plan?: 'capsule' | 'collection' | 'house';
  planName?: string;
  // Tokens the account was given when it was created (before #458).
  starterGrant?: number;
  // The account's pool of styles and codes (#458): a team member gets the
  // owner's. `tokenBalance` stays as its styles and codes left.
  usage?: UsageSnapshot | null;
  // #452: set while the owner's account is scheduled for deletion (the app
  // then shows only the restore screen).
  deletion?: PendingDeletion;
  // #452: a team member's team, by its published brand name.
  teamOwnerName?: string;
}

// Interface for the authentication context
interface AuthContextType {
  isAuthenticated: boolean;
  user: any | null;
  userProfile: UserProfile | null;
  /** An auth call is in flight: the session check, or a form's sign-in, sign-up, reset... */
  isLoading: boolean;
  /** The first session check hasn't settled yet: nobody knows who is signed in. */
  isInitializing: boolean;
  error: string | null;
  login: (email: string, password: string, newPassword?: string) => Promise<void>;
  /** Signs out of every session of the account; `everywhere: false` clears this browser only. */
  logout: (options?: { everywhere?: boolean }) => Promise<void>;
  /** Client (brand) sign-up; `brandName` goes to Cognito as the `name` attribute. */
  register: (email: string, password: string, brandName?: string) => Promise<void>;
  registerConsumer: (email: string, password: string) => Promise<void>;
  confirmUserSignUp: (email: string, code: string) => Promise<void>;
  resendConfirmationCode: (email: string) => Promise<void>;
  forgotPassword: (email: string) => Promise<void>;
  confirmPasswordReset: (email: string, code: string, newPassword: string) => Promise<void>;
  clearError: () => void;
  refetchProfile: () => Promise<void>;
}

// Create the authentication context
/**
 * Sign in, tolerating a session that is already live.
 *
 * Amplify v6 throws `UserAlreadyAuthenticatedException` instead of signing in
 * over an existing session (v5 swapped users silently and leaked state between
 * them). `PublicOnlyRoute` keeps signed-in users off the auth forms, so this is
 * the backstop for the races that guard cannot catch: another tab signed in
 * mid-submit, or a session restored while the form was open (#261).
 *
 * Branches on *who* is signed in:
 *   - same user  -> the login already succeeded; report it as such
 *   - other user -> sign out and retry once ("switch account")
 *
 * Deliberately NOT a blanket signOut-before-signIn: that would destroy a
 * perfectly good session over a mistyped password.
 */
export async function signInHandlingLiveSession(email: string, password: string) {
  try {
    return await signIn({ username: email, password });
  } catch (err: any) {
    if (err?.name !== 'UserAlreadyAuthenticatedException') throw err;

    let currentUser: Awaited<ReturnType<typeof getCurrentUser>> | null = null;
    try {
      currentUser = await getCurrentUser();
    } catch {
      currentUser = null;
    }

    const sameUser =
      !!currentUser &&
      [currentUser.signInDetails?.loginId, currentUser.username].some(
        id => typeof id === 'string' && id.toLowerCase() === email.toLowerCase()
      );

    if (sameUser) {
      // Already signed in as this account — treat it as a completed sign-in
      // rather than scolding someone for being logged in.
      return { isSignedIn: true, nextStep: { signInStep: 'DONE' } } as Awaited<
        ReturnType<typeof signIn>
      >;
    }

    // A different account holds the session: switch to the requested one.
    await signOut();
    return await signIn({ username: email, password });
  }
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Provider component for the authentication context
export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState<any | null>(null);
  const [userProfile, setUserProfile] = useState<UserProfile | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isInitializing, setIsInitializing] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Check authentication status on component mount
  useEffect(() => {
    checkAuthState();
  }, []);

  // A dead session (the API refused the token, or none is left to send) signs
  // the user out once; ProtectedRoute then shows the login page, which says
  // why (#284).
  const authenticatedRef = useRef(false);
  authenticatedRef.current = isAuthenticated;
  const expiringRef = useRef(false);
  // Set below, once refetchProfile exists; the listener reads it when it fires.
  const refetchProfileRef = useRef<(() => Promise<void>) | null>(null);
  const endSession = (reason: SessionEndReason) => {
    if (!authenticatedRef.current || expiringRef.current) return;
    expiringRef.current = true;
    markSessionExpiredNotice(reason);
    // This browser only: the session is already dead or merely idle here.
    logout({ everywhere: false }).finally(() => {
      expiringRef.current = false;
    });
  };
  useEffect(() => {
    const onExpired = (event: Event) => endSession(sessionEndReasonOf(event));
    window.addEventListener(SESSION_EXPIRED_EVENT, onExpired);
    // #452: the account was scheduled for deletion (in another tab, say):
    // reload the profile, which brings up the restore screen.
    const onPendingDeletion = () => {
      refetchProfileRef.current?.();
    };
    window.addEventListener(ACCOUNT_PENDING_DELETION_EVENT, onPendingDeletion);
    return () => {
      window.removeEventListener(SESSION_EXPIRED_EVENT, onExpired);
      window.removeEventListener(ACCOUNT_PENDING_DELETION_EVENT, onPendingDeletion);
    };
    // logout only touches state setters and Amplify; one listener is enough.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 24 h without activity ends the session (#284): stamp activity while signed
  // in, and check now and then (a tab left open for a day).
  useEffect(() => {
    if (!isAuthenticated) return;
    const stopTracking = trackActivity();
    const timer = setInterval(() => {
      if (isIdleExpired()) endSession('idle');
    }, IDLE_CHECK_INTERVAL_MS);
    return () => {
      stopTracking();
      clearInterval(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated]);

  // Fetch user profile from backend
  const fetchUserProfile = async () => {
    try {
      const session = await fetchAuthSession();
      const token = session.tokens?.accessToken?.toString();

      if (!token) {
        return;
      }

      const response = await fetch(`${API_BASE_URL}api/users/me`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const profile = await response.json();
        setUserProfile(profile);
        // PostHog identity: distinct_id = DynamoDB user id, nothing personal
        // (privacy decision 2026-10-31). Sent only once analytics are accepted.
        identifyUser({
          id: profile.id,
          role: profile.role,
          accountType: profile.accountType,
          isSubUser: profile.isSubUser,
        });
      }
    } catch (error) {
      console.error('Failed to fetch user profile:', error);
    }
  };

  // Refetch user profile (useful after token changes)
  const refetchProfile = async () => {
    await fetchUserProfile();
  };
  refetchProfileRef.current = refetchProfile;

  // Check if user is authenticated
  const checkAuthState = async () => {
    try {
      setIsLoading(true);
      const authUser = await getCurrentUser();
      const session = await fetchAuthSession();

      // Back after more than a day away: the session ends here (#284).
      if (session.tokens && isIdleExpired()) {
        markSessionExpiredNotice('idle');
        await signOut().catch(() => undefined);
        throw new Error('Signed out after 24 hours without activity');
      }

      setUser(authUser);
      setIsAuthenticated(!!session.tokens);

      if (session.tokens) {
        // Opening the app counts as activity (and starts the clock for a
        // session signed in before the idle limit existed).
        recordActivity(Date.now(), true);
        await fetchUserProfile();
      }
    } catch (error) {
      // Not authenticated
      setUser(null);
      setUserProfile(null);
      resetIdentity();
      setIsAuthenticated(false);
    } finally {
      setIsLoading(false);
      setIsInitializing(false);
    }
  };

  // Login with email and password
  const login = async (email: string, password: string, newPassword?: string) => {
    try {
      setIsLoading(true);
      setError(null);

      const signInOutput = await signInHandlingLiveSession(email, password);

      // Handle NEW_PASSWORD_REQUIRED challenge (for invited sub-users)
      let signedIn = signInOutput.isSignedIn;
      if (signInOutput.nextStep?.signInStep === 'CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED') {
        if (!newPassword) {
          throw new Error('New password is required for first-time login');
        }

        const { confirmSignIn } = await import('aws-amplify/auth');
        const confirmOutput = await confirmSignIn({ challengeResponse: newPassword });
        signedIn = confirmOutput.isSignedIn;
      }

      if (signedIn) {
        const authUser = await getCurrentUser();
        recordActivity(Date.now(), true);
        setUser(authUser);
        setIsAuthenticated(true);

        await fetchUserProfile();

        // Fire-and-forget login analytics; failures must not block sign-in
        try {
          fetch(`${API_BASE_URL}api/auth/log-event`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              event: 'login',
              email: email,
              source: 'frontend',
            }),
          }).catch(() => { });
        } catch (logError) {
          // Log error but don't fail the login
          console.error('Failed to log login event:', logError);
        }
      }
    } catch (error: any) {
      console.error('Login error:', error);
      setError(error.message || 'Login failed');
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  // Logout user: every session of the account unless told otherwise
  const logout = async ({ everywhere = true }: { everywhere?: boolean } = {}) => {
    try {
      setIsLoading(true);
      if (everywhere) {
        await signOutEverywhere();
      } else {
        await signOut();
      }
      setUser(null);
      setUserProfile(null);
      resetIdentity();
      setIsAuthenticated(false);
      // Drop any pending vault save intent so the next person to sign in on
      // this device doesn't silently save the previous visitor's product.
      sessionStorage.removeItem(VAULT_INTENT_KEY);
      // Same for a pending app-connection consent (#272): it belongs to
      // whoever started it, not to the next account signed in on this tab.
      clearPendingOAuthRequest();
    } catch (error: any) {
      console.error('Logout error:', error);
      setError(error.message || 'Logout failed');
    } finally {
      setIsLoading(false);
    }
  };

  // Register new user
  const register = async (email: string, password: string, brandName?: string) => {
    try {
      setIsLoading(true);
      setError(null);

      // Through the sign-up-only client (pentest AUTH-4, ./cognitoSignUp.ts).
      await signUpWithSignUpClient({
        username: email,
        password,
        attributes: {
          email,
          // The sign-up trigger stores `name` as fullName: the brand name
          // shoppers see until one is set in Settings (#395).
          ...(brandName ? { name: brandName } : {}),
        },
      });

      // Fire-and-forget registration analytics; failures must not block sign-up
      try {
        fetch(`${API_BASE_URL}api/auth/log-event`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            event: 'register',
            email: email,
            source: 'frontend',
          }),
        }).catch(() => { });
      } catch (logError) {
        // Log error but don't fail the registration
        console.error('Failed to log registration event:', logError);
      }
    } catch (error: any) {
      console.error('Registration error:', error);
      setError(error.message || 'Registration failed');
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  // Register a new Consumer Vault account (custom:accountType = 'consumer').
  // Kept separate from `register` (client signup) so existing client signup
  // behavior is untouched.
  const registerConsumer = async (email: string, password: string) => {
    try {
      setIsLoading(true);
      setError(null);

      await signUpWithSignUpClient({
        username: email,
        password,
        attributes: {
          email,
          'custom:accountType': 'consumer',
        },
      });
    } catch (error: any) {
      console.error('Consumer registration error:', error);
      setError(error.message || 'Registration failed');
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  // Confirm registration with verification code
  const confirmUserSignUp = async (email: string, code: string) => {
    try {
      setIsLoading(true);
      setError(null);
      await confirmSignUp({
        username: email,
        confirmationCode: code,
        // Both sign-up forms show the Terms and Privacy Policy notice; the
        // PostConfirmation trigger records this version with the time
        // (backend/src/config/terms.ts).
        options: { clientMetadata: { termsVersion: TERMS_VERSION } },
      });
    } catch (error: any) {
      console.error('Verification error:', error);
      setError(error.message || 'Verification failed');
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  // Resend confirmation code
  const resendConfirmationCode = async (email: string) => {
    try {
      setIsLoading(true);
      setError(null);
      await resendSignUpCode({ username: email });
    } catch (error: any) {
      console.error('Resend code error:', error);
      setError(error.message || 'Failed to resend code');
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  // Request password reset
  const forgotPassword = async (email: string) => {
    try {
      setIsLoading(true);
      setError(null);
      await resetPassword({ username: email });
    } catch (error: any) {
      console.error('Forgot password error:', error);

      // If user is not found, we should still return success to prevent email enumeration
      if (
        error.name === 'UserNotFoundException' ||
        error.message === 'Cannot reset password for the user as there is no registered/verified email or phone_number'
      ) {
        return;
      }

      setError(error.message || 'Failed to send reset code');
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  // Reset password with verification code
  const confirmPasswordReset = async (email: string, code: string, newPassword: string) => {
    try {
      setIsLoading(true);
      setError(null);
      await confirmResetPassword({
        username: email,
        confirmationCode: code,
        newPassword,
      });
    } catch (error: any) {
      console.error('Reset password error:', error);
      setError(error.message || 'Password reset failed');
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  // Clear any error
  const clearError = () => {
    setError(null);
  };

  return (
    <AuthContext.Provider
      value={{
        isAuthenticated,
        user,
        userProfile,
        isLoading,
        isInitializing,
        error,
        login,
        logout,
        register,
        registerConsumer,
        confirmUserSignUp,
        resendConfirmationCode,
        forgotPassword,
        confirmPasswordReset,
        clearError,
        refetchProfile,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

// Custom hook to use auth context
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
